import React from 'react';

import { NextPage } from 'next';

import { Breadcrumb } from '@domains/breadcrumbs';
import { LinkChip } from '@domains/chips';
import { RecipeRubric } from '@domains/recipes';
import { RecipesListPageProps } from '@domains/view';

import { ALL_RECIPES_CHIP_BASE, ALL_RECIPES_CHIP_ID } from '@utils/constants';
import { makeCatalogRoute, makeRecipesRoute, makeRecipesRubricRoute } from '@utils/route';

import { DefaultLayout } from '@layouts/DefaultLayout/DefaultLayout';

import { Breadcrumbs } from '@components/common/Breadcrumbs/Breadcrumbs';
import { Chips, ChipsKind } from '@components/common/Chip/Chips';
import { Container } from '@components/common/Container/Container';
import { InformingBlock } from '@components/common/InformingBlock/InformingBlock';
import { Pagination } from '@components/common/Pagination/Pagination';
import { RecipesGrid } from '@components/common/RecipesGrid/RecipesGrid';

import styles from './RecipesView.module.scss';

const breadcrumbs: Breadcrumb[] = [{ caption: 'Рецепты' }];

export const allRecipesLinkChip = (makeUrl: () => string = makeRecipesRoute): LinkChip => ({
    ...ALL_RECIPES_CHIP_BASE,
    url: makeUrl(),
});

export const mapRubricsToLinkChips = (
    rubrics: RecipeRubric[],
    makeUrl = makeRecipesRubricRoute,
): LinkChip[] =>
    rubrics.map((rubric) => ({
        id: rubric.slug,
        caption: rubric.name,
        url: makeUrl(rubric.slug),
    }));

export const RecipesView: NextPage<RecipesListPageProps> = (props) => {
    const { data } = props;
    const { page, page_size, total, selected_rubric_slug, rubrics, items, seo } = data;

    const chipsData: LinkChip[] = [allRecipesLinkChip(), ...mapRubricsToLinkChips(rubrics)];

    return (
        <DefaultLayout {...seo}>
            <Container>
                <Breadcrumbs items={breadcrumbs} isWithHomeCrumb />
                <h1 className={styles.title}>Рецепты</h1>
            </Container>
            {items.length > 0 && (
                <>
                    <Chips
                        kind={ChipsKind.Links}
                        data={chipsData}
                        activeChipId={selected_rubric_slug ?? ALL_RECIPES_CHIP_ID}
                        className={styles.chips}
                    />
                    <Container>
                        <RecipesGrid recipes={items} />

                        {total > page_size && (
                            <Pagination
                                className={styles.pagination}
                                total={total}
                                pageSize={page_size}
                                currentPage={page}
                                options={{
                                    currentQuery: {
                                        rubric_slug: selected_rubric_slug ?? undefined,
                                    },
                                }}
                            />
                        )}
                    </Container>
                </>
            )}

            {items.length === 0 && (
                <Container>
                    <InformingBlock
                        icon="empty"
                        title={{
                            text: 'Скоро тут появятся рецепты',
                            element: 'h2',
                            marginBottom: 16,
                        }}
                        description={{
                            text: 'Но в каталоге очень много интересного!',
                            color: 'muted',
                            size: 16,
                            marginBottom: 24,
                        }}
                        buttons={[
                            {
                                link: makeCatalogRoute(),
                                variant: 'green',
                                children: 'В каталог',
                            },
                            null,
                        ]}
                        className={styles.informing}
                    />
                </Container>
            )}
        </DefaultLayout>
    );
};
